// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing — the basemaps the passage map offers, and small coordinate helpers.
// Charts and imagery come from the shared registry (@vortex/basemaps) through our CDN where
// its terms allow, so the licence notes live in one place; Google satellite is the portal's
// existing fleet-map layer.

import { tileLayerOptions } from '@vortex/basemaps';
import { BASEMAP_URLS } from '../../utils/constants';
import type { LatLon } from '../../services/api';

export interface Basemap {
  id: string;
  label: string;
  url: string;
  attribution: string;
  maxZoom: number;
  maxNativeZoom?: number;
  /** The tile front door source, when its tiles go through it (the url is then the fallback). */
  source?: string;
}

function fromRegistry(id: string, label: string): Basemap {
  const o = tileLayerOptions(id);
  return { id, label, url: o.url, attribution: o.attribution, maxZoom: o.maxZoom, maxNativeZoom: o.maxNativeZoom, source: id };
}

/** The choice of base layers, first is the default. */
export const BASEMAPS: Basemap[] = [
  fromRegistry('osm', 'Map (OpenStreetMap)'),
  fromRegistry('esri-imagery', 'Satellite (Esri)'),
  { id: 'google-satellite', label: 'Satellite (Google)', url: BASEMAP_URLS.GOOGLE_SATELLITE, attribution: '© Google Maps', maxZoom: 20 },
];

/** Buoys, lights and marks over any base layer. */
export const SEAMARKS: Basemap = { ...fromRegistry('openseamap-seamark', 'Seamarks (OpenSeaMap)'), source: undefined };

export function basemapById(id: string): Basemap {
  return BASEMAPS.find((b) => b.id === id) ?? BASEMAPS[0];
}

/** Longitude into [−180, 180) — a click on a wrapped map can be far outside it. */
export function wrapLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

/** A clicked or dragged position, wrapped and rounded to 4 decimals (about 10 m). */
export function cleanPoint(lat: number, lon: number): LatLon {
  const r = (x: number) => Math.round(x * 1e4) / 1e4;
  return [r(Math.max(-89.9, Math.min(89.9, lat))), r(wrapLon(lon))];
}

/** The passage's points in order, with longitudes kept continuous for drawing. */
export function passageLine(points: LatLon[]): LatLon[] {
  const out: LatLon[] = [];
  for (const [lat, lon] of points) {
    let l = lon;
    if (out.length) {
      const prev = out[out.length - 1][1];
      while (l - prev > 180) l -= 360;
      while (l - prev < -180) l += 360;
    }
    out.push([lat, l]);
  }
  return out;
}

export interface Preset {
  id: string;
  label: string;
  start: LatLon;
  end: LatLon;
  note: string;
}

export const PRESETS: Preset[] = [
  {
    id: 'noumea-opua',
    label: 'Nouméa → Opua',
    // Open water south of the New Caledonia lagoon, and about 17 nm north-east of Cape Brett.
    start: [-23.4, 166.9],
    end: [-34.95, 174.6],
    note: 'Demonstration gates in open water outside the lagoon and the Bay of Islands — not pass or harbour positions.',
  },
];
